import { Events } from './events';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

type ImageTuple = [string, File]; // [fileName, file]

// Accept spec for showOpenFilePicker
const ImageFileTypes: FilePickerAcceptType[] = [{
    description: 'Images',
    accept: {
        'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp']
    }
}];

type MultiFileSelectorCallback = (files: File[] | null) => void;

// Helper class to show a multi-file selector dialog when showOpenFilePicker is unavailable.
class MultiFileSelector {
    show: (callbackFunc: MultiFileSelectorCallback) => void;

    constructor() {
        const input = document.createElement('input');
        input.setAttribute('id', 'image-file-selector');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.setAttribute('multiple', 'true');

        // Keep it off-DOM visually but in the doc for click()
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.append(input);

        let callbackFunc: MultiFileSelectorCallback = null;

        input.addEventListener('change', () => {
            const selected = input.files ? Array.from(input.files) : [];
            // allow re-selecting the same files again later
            input.value = '';
            callbackFunc?.(selected.length ? selected : null);
        });

        // Not all browsers fire 'cancel' for <input type=file>, but keep parity with your pattern
        input.addEventListener('cancel', () => {
            callbackFunc?.(null);
        });

        this.show = (func: MultiFileSelectorCallback) => {
            callbackFunc = func;
            input.click();
        };
    }
}

const registerImageImportEvents = (events: Events) => {
    // Construct the file selector (fallback if File System Access API unavailable)
    const fileSelector = (window as any).showOpenFilePicker ? null : new MultiFileSelector();

    // In-memory store of tuples [fileName, file]
    const importedImages: ImageTuple[] = [];

    const isImage = (f: File) => f && typeof f.type === 'string' && f.type.startsWith('image/');

    const toTuples = (files: File[]): ImageTuple[] =>
        files.filter(isImage).map((f) => [f.name, f]);

    // --- Public API via events ---

    // Opens a file picker and imports images; returns ImageTuple[]
    events.function('images.open', async () => {
        if (fileSelector) {
            // Fallback path using <input type=file multiple>
            return await new Promise<ImageTuple[] | false>((resolve) => {
                fileSelector.show((files) => {
                    if (!files || files.length === 0) return resolve(false);
                    const tuples = toTuples(files);
                    if (tuples.length === 0) return resolve(false);
                    importedImages.push(...tuples);
                    events.fire('images.added', tuples);
                    resolve(tuples);
                });
            });
        } else {
            // Modern path using File System Access API
            try {
                const handles: FileSystemFileHandle[] = await (window as any).showOpenFilePicker({
                    id: 'ImageFilesOpen',
                    multiple: true,
                    types: ImageFileTypes
                });

                if (!handles?.length) return false;

                const files = await Promise.all(handles.map((h) => h.getFile()));
                const tuples = toTuples(files);
                if (!tuples.length) return false;

                importedImages.push(...tuples);
                events.fire('images.added', tuples);
                return tuples;
            } catch (error: any) {
                if (error?.name !== 'AbortError') console.error(error);
                return false;
            }
        }
    });

    // Allows programmatic import (e.g., from drag & drop): pass File[] or FileList
    events.function('images.load', async (filesLike: File[] | FileList) => {
        const files = Array.isArray(filesLike) ? filesLike : Array.from(filesLike);
        const tuples = toTuples(files);
        if (!tuples.length) return false;
        importedImages.push(...tuples);
        events.fire('images.added', tuples);
        return tuples;
    });

    // Returns a shallow copy of all imported image tuples
    events.function('images.all', () => {
        return importedImages.slice();
    });

    // Clears the store
    events.function('images.clear', () => {
        importedImages.length = 0;
        events.fire('images.cleared');
        return true;
    });
};

export { registerImageImportEvents };
export type { ImageTuple };
